import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    inject
} from "@angular/core";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IDateDrillDownSelectedItem } from "~app/common/components/date-drill-down/components/date-drill-down-selector/models/dateDrillDownSelectedItem";
import { Cellstyle, NumericCellChangeArg } from "~app/common/components/hot-doc/hotdoc/cell";
import { HotDoc, IHotColumnDefinition, IHotDocSettings } from "~app/common/components/hot-doc/hotdoc/doc";
import { Expander } from "~app/common/components/hot-doc/hotdoc/expander";
import { HotRow } from "~app/common/components/hot-doc/hotdoc/row";
import { ToastService } from "~app/common/components/toast/toast.service";
import { AggregatPeriods, IAggregatPeriod } from "~app/common/models/aggregatperiod";
import { CommandService } from "~app/common/services/cqs/command.service";
import { FordelaService } from "~app/common/services/fordela.service";
import { SetForsorjningsradPlanProduktionsomradeCommand } from "~app/features/justera-leveransplan/common/commands/setForsorjningsradPlanCommands";
import { SetOverenskommenKvantitetProduktionsomradeCommand } from "~app/features/justera-leveransplan/common/commands/setOverenskommenKvantitetCommands";
import { IJlpResultSettings } from "~app/features/justera-leveransplan/common/models/jlp-model";

import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { DateDrillDownComponent } from "@components/date-drill-down/date-drill-down.component";
import { FooterComponent } from "@components/footer/footer.component";
import { HotDocComponent } from "@components/hot-doc/hot-doc.component";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import {
    ProduktionsomradeAggregat,
    ProduktionsomradeKundorderradAggregat,
    ProduktionsomradeRadAggregat
} from "~app/features/justera-leveransplan/common/models/jlp-produktionsomrade-model";
import { GridPeriod, IOrderProduktionsomradeSearchResponse } from "~app/features/justera-leveransplan/common/models/justeraLeveransplanModel";
import { JlpPeriodService } from "~app/features/justera-leveransplan/common/services/jlp-period.service";
import { KundorderradCell } from "~app/features/justera-leveransplan/common/services/jlp-produktionsomraden.hot-cells";
import { HotkeyDirective } from "../../../../../common/directives/hotkey/hotkey.directive";

/* eslint-disable no-bitwise */
@Component({
    selector: "rask-jlp-produktionsomraden-result",
    templateUrl: "./jlp-produktionsomraden-result.component.html",
    styleUrls: ["./jlp-produktionsomraden-result.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DateDrillDownComponent,
        CheckboxComponent,
        HotDocComponent,
        FooterComponent,
        ButtonComponent,
        HotkeyDirective,
        NgbTooltip,
        TranslatePipe
    ]
})
export class JlpProduktionsomradenResultComponent implements OnChanges {
    @Input() canEdit: boolean;
    currentPeriod: IAggregatPeriod = null;
    @Input() data: IOrderProduktionsomradeSearchResponse;
    hotDoc: HotDoc;
    @Input() period: GridPeriod;
    @Input() periodFrom: any;
    @Input() periodTom: any;
    @Input() senastAktivaProduktionsomraden: number[];
    @Input() settings: IJlpResultSettings;
    showTakt = true;

    private readonly $translate = inject(TranslateService);
    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly fordelaKvantitetService = inject(FordelaService);
    private readonly jlpPeriodService = inject(JlpPeriodService);
    private readonly toastService = inject(ToastService);

    private aggregatePeriods: IAggregatPeriod[];
    private model: ProduktionsomradeAggregat;

    constructor() {
        const commandService = this.commandService;

        this.commandService = commandService.createInstance();
    }

    canSave(): boolean {
        return this.commandService.isDirty();
    }

    dagarKvarInnevarandeManad(settings: IJlpResultSettings): number {
        return settings.totaltAntalArbetsdagarInnevarandeManad - Math.round(settings.passeradeAntalArbetsdagarInnevarandeManad);
    }

    initHotDoc = () => {
        this.prepareHotDoc(this.data);
        this.hotDoc = this.buildHotDoc();
    };

    isDirty(): boolean {
        return this.commandService.isDirty();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.settings !== undefined) {
            this.settings = changes.settings.currentValue;
        }

        this.initHotDoc();
    }

    onDateSelected(item: IDateDrillDownSelectedItem) {
        this.period = this.jlpPeriodService.onDateSelected(item);
        this.prepareHotDoc(this.data);
        this.hotDoc.rebind();
    }

    save(): void {
        this.busyService
            .addWork(this.commandService.pushCommandQueueTransaction())
            .subscribe(() => this.toastService.add("Ändringarna sparades"));
    }

    toggleTakt() {
        this.hotDoc.bind();
    }

    private readonly buildColumnDefinitions = (periods: IAggregatPeriod[]): IHotColumnDefinition[] => {
        const columns: IHotColumnDefinition[] = [];

        columns.push({
            header: "",
            width: 220,
            readOnly: true,
            cellName: "rowHeader1"
        });

        columns.push({
            header: "",
            width: 100,
            readOnly: true,
            cellName: "rowHeader2"
        });

        if (this.showTakt) {
            columns.push({
                header: "Takt",
                width: 60,
                readOnly: true,
                cellName: "takt",
                type: "numeric"
            });
        }

        const periodCols = periods.map(period => ({
            header: period.name,
            width: 95,
            cellName: period.key,
            type: "numeric"
        }));
        columns.push(...periodCols);

        columns.push({
            header: this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA"),
            cellName: "summa",
            type: "numeric",
            width: 95
        });

        columns.push({
            header: this.$translate.instant("KUNDORDER_LEVRADER_HEADER_TRANSPORTERAT_LEVERANSRAD"),
            cellName: "transporteratleveransrad",
            type: "numeric",
            width: 140
        });

        columns.push({
            header: this.$translate.instant("KUNDORDER_LEVRADER_HEADER_OVERENSKOMMET"),
            cellName: "overenskommet",
            type: "numeric",
            width: 130
        });

        columns.push({
            header: this.$translate.instant("KUNDORDER_LEVRADER_HEADER_AVVIKELSE"),
            cellName: "avvikelse",
            type: "numeric",
            width: 95
        });

        return columns;
    };

    private readonly onUpdateForsorjningsradPlan = (model: ProduktionsomradeKundorderradAggregat, period: IAggregatPeriod, change: NumericCellChangeArg) => {
        const periodStart = period.getStartOrLater(model.periodFrom);
        const periodEnd = period.getEndOrEarlier(model.periodTom);

        this.fordelaKvantitetService.getFordeladKvantitet(change.newValue, periodStart, periodEnd)
            .subscribe(result => {
                model.updateForsorjningsradPlan(result);

                const command = new SetForsorjningsradPlanProduktionsomradeCommand(c => {
                    c.kundorderradId = model.kundorderradId;
                    c.produktionsomradeId = model.produktionsomradeId;
                    c.periodStart = periodStart;
                    c.periodEnd = periodEnd;
                    c.fordeladKvantitet = result;
                });
                this.commandService.addTransactionCommand(command);

                change.complete();
            });
    };

    private readonly onUpdateOverenskommenKvantitet = (model: ProduktionsomradeKundorderradAggregat, change: NumericCellChangeArg) => {
        model.overenskommet = change.newValue;

        const command = new SetOverenskommenKvantitetProduktionsomradeCommand(c => {
            c.kundorderradId = model.kundorderradId;
            c.produktionsomradeId = model.produktionsomradeId;
            c.kvantitet = change.newValue;
        });
        this.commandService.addTransactionCommand(command);

        change.complete();
    };

    private readonly buildKundorderradPlanrad = (row: HotRow, model: ProduktionsomradeKundorderradAggregat, periods: IAggregatPeriod[]): void => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.White | Cellstyle.NoRightBorder | Cellstyle.NoBottomBorder);
        row.add("rowHeader1", KundorderradCell.create(model));
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_PLAN"), Cellstyle.Readonly | Cellstyle.White | Cellstyle.NoBottomBorder);

        const percentSettings = { noZero: true };
        row.addPercent("takt", model.calculateTakt(this.currentPeriod, this.settings.passeradeAntalArbetsdagarInnevarandeManad,
            this.settings.totaltAntalArbetsdagarInnevarandeManad),
        Cellstyle.Readonly, percentSettings);

        periods.forEach(period => {
            const total = model.sumPlan(period);
            const isActive = period.isWithin(model.periodFrom, model.periodTom);
            const style = isActive ? 0 : Cellstyle.Readonly;
            row.addNumeric(period.key, total, style, change => this.onUpdateForsorjningsradPlan(model, period, change));
        });

        row.addNumeric("transporteratleveransrad", model.transporteratLeveransrad(periods), Cellstyle.Readonly);
        row.addNumeric("overenskommet", model.overenskommet, null, change => this.onUpdateOverenskommenKvantitet(model, change));
        row.addNumeric("avvikelse", model.avvikelse(periods), Cellstyle.Readonly);
    };

    private readonly buildKundorderradTransporteratrad = (row: HotRow, model: ProduktionsomradeKundorderradAggregat, periods: IAggregatPeriod[]): void => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.White);
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_TRANSPORTERAT"), Cellstyle.Readonly | Cellstyle.White);

        let grandtotal = 0;
        periods.forEach(period => {
            const total = model.sumTransporterat(period);
            grandtotal += total;
            row.addNumeric(period.key, total, Cellstyle.Readonly);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
    };

    private readonly buildProduktionsomradeSummaPlanrad = (row: HotRow, expander: Expander, model: ProduktionsomradeRadAggregat, periods: IAggregatPeriod[]): void => {
        row.addExpander(expander, Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.AlignLeft, { rowSpan: 2 });

        row.addText("rowHeader1", model.namn, Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.Uppercase, { rowSpan: 2 });
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA_PLAN"), Cellstyle.Readonly | Cellstyle.NoBottomBorder);

        const percentSettings = { noZero: true };
        row.addPercent("takt", model.calculateTakt(this.currentPeriod, this.settings.passeradeAntalArbetsdagarInnevarandeManad,
            this.settings.totaltAntalArbetsdagarInnevarandeManad),
        Cellstyle.Readonly, percentSettings);

        periods.forEach(period => {
            const sumPlan = model.sumPlan(period);
            row.addNumeric(period.key, sumPlan, Cellstyle.Readonly);
        });
        row.addNumeric("summa", null, Cellstyle.Readonly);
        row.addNumeric("transporteratleveransrad", model.transporteratLeveransrad(periods), Cellstyle.Readonly);
        row.addNumeric("overenskommet", model.overenskommet(), Cellstyle.Readonly);
        row.addNumeric("avvikelse", model.avvikelse(periods), Cellstyle.Readonly);
    };

    private readonly buildProduktionsomradeSummaTransporteratrad = (row: HotRow, model: ProduktionsomradeRadAggregat, periods: IAggregatPeriod[]): void => {
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA_TRANSPORTERAT"), Cellstyle.Readonly);

        let grandtotal = 0;
        periods.forEach(period => {
            const sumPlan = model.sumTransporterat(period);
            grandtotal += sumPlan;
            row.addNumeric(period.key, sumPlan, Cellstyle.Readonly);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
    };

    private readonly buildSummaPlan = (row: HotRow, model: ProduktionsomradeAggregat, periods: IAggregatPeriod[]) => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.NoBottomBorder);
        row.addText("rowHeader1", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA"),
            Cellstyle.Readonly | Cellstyle.Uppercase | Cellstyle.Bold | Cellstyle.NoBottomBorder | Cellstyle.NoRightBorder);
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA_PLAN"), Cellstyle.Readonly | Cellstyle.Bold | Cellstyle.NoBottomBorder);

        const percentSettings = { noZero: true };
        row.addPercent("takt", model.calculateTakt(this.currentPeriod, this.settings.passeradeAntalArbetsdagarInnevarandeManad,
            this.settings.totaltAntalArbetsdagarInnevarandeManad),
        Cellstyle.Readonly, percentSettings);

        periods.forEach(period => {
            const sumPeriod = model.sumPlan(period);
            row.addNumeric(period.key, sumPeriod, Cellstyle.Readonly | Cellstyle.Bold);
        });

        row.addText("summa", "", Cellstyle.Readonly);
        row.addNumeric("transporteratleveransrad", model.transporteratLeveransrad(periods), Cellstyle.Readonly);
        row.addText("overenskommet", "", Cellstyle.Readonly);
        row.addText("avvikelse", "", Cellstyle.Readonly);
    };

    private readonly buildSummaTransporterat = (row: HotRow, model: ProduktionsomradeAggregat, periods: IAggregatPeriod[]) => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder);
        row.addEmpty("rowHeader1", Cellstyle.Readonly | Cellstyle.NoRightBorder);
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA_TRANSPORTERAT"), Cellstyle.Readonly | Cellstyle.Bold);

        let grandtotal = 0;
        periods.forEach(period => {
            const sumPeriod = model.sumTransporterat(period);
            grandtotal += sumPeriod;
            row.addNumeric(period.key, sumPeriod, Cellstyle.Readonly | Cellstyle.Bold);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
    };

    private readonly buildHotDoc = (): HotDoc => {
        const hotDocSettings: IHotDocSettings = {
            rowHeights: 40,
            raskSettings: {
                expander: {
                    col: 0,
                    name: "expanders"
                },
                calculateSum: true
            },
            fixedColumnsStart: 3
        };

        const colFactory = () => this.buildColumnDefinitions(this.aggregatePeriods);

        const rowFactory = (doc: HotDoc) => {
            const model = this.model;
            const periods = this.aggregatePeriods;

            model.produktionsomraderader.forEach(produktionsomraderad => {
                const expander = doc.createExpander(produktionsomraderad.id);
                doc.createRow(row => this.buildProduktionsomradeSummaPlanrad(row, expander, produktionsomraderad, periods), Cellstyle.StrongTop);
                doc.createRow(row => this.buildProduktionsomradeSummaTransporteratrad(row, produktionsomraderad, periods));

                if (expander.expanded) {
                    produktionsomraderad.kundorderrader.forEach(kundorderrad => {

                        doc.createRow(row => this.buildKundorderradPlanrad(row, kundorderrad, periods));

                        doc.createRow(row => this.buildKundorderradTransporteratrad(row, kundorderrad, periods));
                    });
                }
            });

            doc.createRow(row => this.buildSummaPlan(row, model, periods), Cellstyle.StrongTop);
            doc.createRow(row => this.buildSummaTransporterat(row, model, periods));
        };

        return new HotDoc(colFactory, rowFactory, () => hotDocSettings);
    };

    private prepareHotDoc(res: IOrderProduktionsomradeSearchResponse) {
        this.aggregatePeriods = AggregatPeriods.create("sv-SE", this.period);
        this.currentPeriod = this.aggregatePeriods.find(a => a.isCurrentlyActive) || null;
        this.model = new ProduktionsomradeAggregat(this.senastAktivaProduktionsomraden, res.kundorderrader);
    }
}
